import { useSyncExternalStore } from 'react'

import channelManifest from './channelManifest.json'

export type DesignSystemReleaseChannel = 'live' | 'latest'
export type DesignSystemChannel = DesignSystemReleaseChannel | 'experimental'
export type DesignSystemExperimentalVariant = 'compact' | 'morph'

export type DesignSystemChannelVersion = {
  sourceRepo: string
  sourceSha: string | null
  releaseTag: string | null
  manifestHash: string | null
  label: string
  promotedAt: string | null
  syncedAt: string | null
}

export type DesignSystemChannelManifest = {
  schemaVersion: 1
  defaultChannel: DesignSystemReleaseChannel
  channels: Record<DesignSystemReleaseChannel, DesignSystemChannelVersion>
}

export const DESIGN_SYSTEM_CHANNEL_STORAGE_KEY = 'EXT_DESIGN_SYSTEM_CHANNEL'
export const DESIGN_SYSTEM_CHANNEL_CHANGE_EVENT =
  'ext-design-system-channel-change'
export const DESIGN_SYSTEM_EXPERIMENTAL_VARIANT_STORAGE_KEY =
  'EXT_DESIGN_SYSTEM_EXPERIMENTAL_VARIANT'
export const DESIGN_SYSTEM_EXPERIMENTAL_VARIANT_CHANGE_EVENT =
  'ext-design-system-experimental-variant-change'

const manifest = channelManifest as DesignSystemChannelManifest
const DEFAULT_EXPERIMENTAL_VARIANT: DesignSystemExperimentalVariant = 'compact'

export function getDesignSystemVersions(): DesignSystemChannelManifest {
  return manifest
}

export function isDesignSystemChannel(
  value: unknown,
): value is DesignSystemChannel {
  return value === 'live' || value === 'latest' || value === 'experimental'
}

export function isDesignSystemExperimentalVariant(
  value: unknown,
): value is DesignSystemExperimentalVariant {
  return value === 'compact' || value === 'morph'
}

function getStoredValue(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function setStoredValue(key: string, value: string): void {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // Local preview controls should keep working when storage is unavailable.
  }
}

export function getDesignSystemChannel(): DesignSystemChannel {
  if (typeof window === 'undefined') {
    return manifest.defaultChannel
  }

  const storedChannel = getStoredValue(DESIGN_SYSTEM_CHANNEL_STORAGE_KEY)

  return isDesignSystemChannel(storedChannel)
    ? storedChannel
    : manifest.defaultChannel
}

export function resolveDesignSystemReleaseChannel(
  channel: DesignSystemChannel = getDesignSystemChannel(),
): DesignSystemReleaseChannel {
  return channel === 'experimental' ? 'latest' : channel
}

export function setDesignSystemChannel(channel: DesignSystemChannel): void {
  if (typeof window === 'undefined') {
    return
  }

  setStoredValue(DESIGN_SYSTEM_CHANNEL_STORAGE_KEY, channel)
  window.dispatchEvent(
    new CustomEvent(DESIGN_SYSTEM_CHANNEL_CHANGE_EVENT, {
      detail: { channel },
    }),
  )
}

export function getDesignSystemExperimentalVariant(): DesignSystemExperimentalVariant {
  if (typeof window === 'undefined') {
    return DEFAULT_EXPERIMENTAL_VARIANT
  }

  const storedVariant = getStoredValue(
    DESIGN_SYSTEM_EXPERIMENTAL_VARIANT_STORAGE_KEY,
  )

  return isDesignSystemExperimentalVariant(storedVariant)
    ? storedVariant
    : DEFAULT_EXPERIMENTAL_VARIANT
}

export function setDesignSystemExperimentalVariant(
  variant: DesignSystemExperimentalVariant,
): void {
  if (typeof window === 'undefined') {
    return
  }

  setStoredValue(DESIGN_SYSTEM_EXPERIMENTAL_VARIANT_STORAGE_KEY, variant)
  window.dispatchEvent(
    new CustomEvent(DESIGN_SYSTEM_EXPERIMENTAL_VARIANT_CHANGE_EVENT, {
      detail: { variant },
    }),
  )
}

export function subscribeToDesignSystemChannel(
  callback: (channel: DesignSystemChannel) => void,
): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const handleChange = (event?: Event) => {
    if (
      event instanceof StorageEvent &&
      event.key !== DESIGN_SYSTEM_CHANNEL_STORAGE_KEY
    ) {
      return
    }

    callback(getDesignSystemChannel())
  }

  window.addEventListener(DESIGN_SYSTEM_CHANNEL_CHANGE_EVENT, handleChange)
  window.addEventListener('storage', handleChange)

  return () => {
    window.removeEventListener(DESIGN_SYSTEM_CHANNEL_CHANGE_EVENT, handleChange)
    window.removeEventListener('storage', handleChange)
  }
}

export function subscribeToDesignSystemExperimentalVariant(
  callback: (variant: DesignSystemExperimentalVariant) => void,
): () => void {
  if (typeof window === 'undefined') {
    return () => {}
  }

  const handleChange = (event?: Event) => {
    if (
      event instanceof StorageEvent &&
      event.key !== DESIGN_SYSTEM_EXPERIMENTAL_VARIANT_STORAGE_KEY
    ) {
      return
    }

    callback(getDesignSystemExperimentalVariant())
  }

  window.addEventListener(
    DESIGN_SYSTEM_EXPERIMENTAL_VARIANT_CHANGE_EVENT,
    handleChange,
  )
  window.addEventListener('storage', handleChange)

  return () => {
    window.removeEventListener(
      DESIGN_SYSTEM_EXPERIMENTAL_VARIANT_CHANGE_EVENT,
      handleChange,
    )
    window.removeEventListener('storage', handleChange)
  }
}

export function useResolvedDesignSystemComponent<TComponent>(
  liveComponent: TComponent,
  latestComponent?: TComponent,
): TComponent {
  const channel = useSyncExternalStore(
    subscribeToDesignSystemChannel,
    getDesignSystemChannel,
    () => manifest.defaultChannel,
  )

  return resolveDesignSystemReleaseChannel(channel) === 'latest' &&
    latestComponent
    ? latestComponent
    : liveComponent
}

/**
 * Resolves the selected design system component for non-React contexts.
 * Do not call this at module scope in generated React facades because it will
 * freeze the selected channel at import time. Use
 * `useResolvedDesignSystemComponent` inside React components instead.
 */
export function resolveDesignSystemComponent<TComponent>(
  liveComponent: TComponent,
  latestComponent?: TComponent,
): TComponent {
  if (resolveDesignSystemReleaseChannel() === 'latest' && latestComponent) {
    return latestComponent
  }

  return liveComponent
}
